import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getOccasions, getOccasionTypes, getOccasionById, updateOccasion, deleteOccasion } from "wasp/client/operations";
import { Plus, Search, Calendar } from "lucide-react";
import DefaultLayout from "./layout/DefaultLayout";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { useFixedPageStack } from "../client/hooks/useFixedPageStack";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../hooks/use-toast";

export default function OccasionsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: occasionTypes = [] } = useQuery(getOccasionTypes);
  const { data: occasions = [], isLoading, error, refetch } = useQuery(getOccasions, {});

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("all");
  const [selectedOccasionId, setSelectedOccasionId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    name: '',
    slug: '',
    occasionTypeId: '',
    description: '',
    significance: '',
    communityMonth: '',
    communityDay: '',
    durationDays: '',
    imageUrl: '',
    isActive: true
  });

  // Fetch selected occasion details for flyout
  const { data: selectedOccasion, isLoading: loadingOccasion } = useQuery(
    getOccasionById,
    { id: selectedOccasionId || '' },
    { enabled: !!selectedOccasionId, keepPreviousData: true }
  );

  const filteredData = useMemo(() => {
    return (occasions as any[]).filter((occasion: any) => {
      if (searchTerm &&
          !occasion.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !occasion.slug.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (selectedTypeId && selectedTypeId !== "all" && occasion.occasionTypeId !== selectedTypeId) {
        return false;
      }
      return true;
    });
  }, [occasions, searchTerm, selectedTypeId]);

  const hasData = filteredData.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: hasData ? 'table-filter' : 'none',
    secondaryRefDeps: [hasData, searchTerm, selectedTypeId],
  });

  // Initialize edit fields when occasion is selected
  useEffect(() => {
    if (selectedOccasion) {
      const occ = selectedOccasion as any;
      setEditFormData({
        name: occ.name || '',
        slug: occ.slug || '',
        occasionTypeId: occ.occasionTypeId || '',
        description: occ.description || '',
        significance: occ.significance || '',
        communityMonth: occ.communityMonth?.toString() || '',
        communityDay: occ.communityDay?.toString() || '',
        durationDays: occ.durationDays?.toString() || '',
        imageUrl: occ.imageUrl || '',
        isActive: occ.isActive ?? true
      });
    }
  }, [selectedOccasion]);

  // Check if any changes have been made
  const occasionData = selectedOccasion as any;
  const hasChanges = occasionData ? (
    editFormData.name !== (occasionData.name || '') ||
    editFormData.slug !== (occasionData.slug || '') ||
    editFormData.occasionTypeId !== (occasionData.occasionTypeId || '') ||
    editFormData.description !== (occasionData.description || '') ||
    editFormData.significance !== (occasionData.significance || '') ||
    editFormData.communityMonth !== (occasionData.communityMonth?.toString() || '') ||
    editFormData.communityDay !== (occasionData.communityDay?.toString() || '') ||
    editFormData.durationDays !== (occasionData.durationDays?.toString() || '') ||
    editFormData.imageUrl !== (occasionData.imageUrl || '') ||
    editFormData.isActive !== (occasionData.isActive ?? true)
  ) : false;

  const handleOccasionClick = (occasionId: string) => {
    setSelectedOccasionId(occasionId);
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!occasionData) return;

    try {
      await updateOccasion({
        id: occasionData.id,
        ...editFormData,
        communityMonth: editFormData.communityMonth ? parseInt(editFormData.communityMonth) : undefined,
        communityDay: editFormData.communityDay ? parseInt(editFormData.communityDay) : undefined,
        durationDays: editFormData.durationDays ? parseInt(editFormData.durationDays) : undefined,
      });

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "Occasion updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update occasion",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!occasionData) return;
    if (confirm('Are you sure you want to delete this occasion? This action cannot be undone.')) {
      try {
        await deleteOccasion({ id: occasionData.id });
        setSelectedOccasionId(null);
        await refetch();
        toast({
          title: "Success",
          description: "Occasion deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: 'Failed to delete occasion: ' + (error as Error).message,
          variant: "destructive",
        });
      }
    }
  };

  // Navigation helpers
  const currentOccasionIndex = filteredData.findIndex(o => o.id === selectedOccasionId) ?? -1;
  const hasPrevious = currentOccasionIndex > 0;
  const hasNext = currentOccasionIndex >= 0 && currentOccasionIndex < filteredData.length - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && filteredData) {
      const previousOccasion = filteredData[currentOccasionIndex - 1];
      setSelectedOccasionId(previousOccasion.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && filteredData) {
      const nextOccasion = filteredData[currentOccasionIndex + 1];
      setSelectedOccasionId(nextOccasion.id);
    }
  };

  // Format date helper
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'Not set';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <DefaultLayout user={user}>
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Occasions</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage religious and cultural occasions ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => navigate('/admin/occasions/new')}
                  icon={Plus}
                  label="Create Occasion"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-[var(--sidebar-width)] right-0 z-20`}
            onHeightChange={fixedStack.onTableFilterHeightChange}
            columns={[
              { title: 'Name', span: 3 },
              { title: 'Type', span: 2 },
              { title: 'Community Date', span: 2 },
              { title: 'Start Date', span: 2 },
              { title: 'Status', span: 3 }
            ]}
            filters={[
              {
                span: 6,
                content: (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by name or slug..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )
              },
              {
                span: 6,
                content: (
                  <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Filter by type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {(occasionTypes as any[]).map((type: any) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }
            ]}
          />
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading occasions: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No occasions found</p>
                <p className="text-sm mt-2">
                  {searchTerm || (selectedTypeId && selectedTypeId !== "all") ? "Try adjusting your filters" : "Click 'Create Occasion' to add your first occasion"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((occasion: any) => (
                  <div
                    key={occasion.id}
                    className="px-4 py-2.5 admin-hoverable transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => handleOccasionClick(occasion.id)}
                    title="Click to view details"
                  >
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
                        {occasion.name}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {occasion.occasionType?.label || 'N/A'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {occasion.communityMonth && occasion.communityDay
                          ? `${occasion.communityMonth}/${occasion.communityDay}`
                          : '-'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {formatDate(occasion.startDate)}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <span className={`text-sm ${occasion.isActive ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                        {occasion.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedOccasionId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedOccasionId(null);
              setFlyoutMode('view');
            }
          }}
          title={flyoutMode === 'view' ? 'View Occasion' : 'Edit Occasion'}
          description={occasionData?.name || undefined}
          width="xl"
          mode={flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === 'view' ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button onClick={handleEdit} size="sm" className="w-[110px]">
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode('view')}
                  className="w-[110px]"
                >
                  {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingOccasion && !selectedOccasion ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : occasionData ? (
            <>
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Name"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.name}
                          onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter occasion name"
                        />
                      ) : (
                        <span>{occasionData.name}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Slug"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.slug}
                          onChange={(e) => setEditFormData({ ...editFormData, slug: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter slug (URL-friendly)"
                        />
                      ) : (
                        <span>{occasionData.slug}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Type"
                    value={
                      flyoutMode === 'edit' ? (
                        <Select
                          value={editFormData.occasionTypeId}
                          onValueChange={(value) => setEditFormData({ ...editFormData, occasionTypeId: value })}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {(occasionTypes as any[]).map((type: any) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{occasionData.occasionType?.label || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter description"
                        />
                      ) : (
                        <span>{occasionData.description || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Significance"
                    value={
                      flyoutMode === 'edit' ? (
                        <Textarea
                          value={editFormData.significance}
                          onChange={(e) => setEditFormData({ ...editFormData, significance: e.target.value })}
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter religious/cultural significance"
                        />
                      ) : (
                        <span>{occasionData.significance || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Community Calendar Section */}
                <FlyoutGridSection title="Community CALENDAR">
                  <FlyoutGridRow
                    label="Community Month"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          type="number"
                          min="1"
                          max="12"
                          value={editFormData.communityMonth}
                          onChange={(e) => setEditFormData({ ...editFormData, communityMonth: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="1-12"
                        />
                      ) : (
                        <span>{occasionData.communityMonth || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Community Day"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          value={editFormData.communityDay}
                          onChange={(e) => setEditFormData({ ...editFormData, communityDay: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="1-30"
                        />
                      ) : (
                        <span>{occasionData.communityDay || 'None'}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Duration (Days)"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          type="number"
                          min="1"
                          value={editFormData.durationDays}
                          onChange={(e) => setEditFormData({ ...editFormData, durationDays: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter duration"
                        />
                      ) : (
                        <span>{occasionData.durationDays || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Gregorian Dates Section */}
                <FlyoutGridSection title="GREGORIAN DATES">
                  <FlyoutGridRow
                    label="Start Date"
                    value={<span>{formatDate(occasionData.startDate)}</span>}
                  />
                  <FlyoutGridRow
                    label="End Date"
                    value={<span>{formatDate(occasionData.endDate)}</span>}
                    isLast
                  />
                </FlyoutGridSection>

                {/* Media Section */}
                <FlyoutGridSection title="MEDIA">
                  <FlyoutGridRow
                    label="Image URL"
                    value={
                      flyoutMode === 'edit' ? (
                        <Input
                          value={editFormData.imageUrl}
                          onChange={(e) => setEditFormData({ ...editFormData, imageUrl: e.target.value })}
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter image URL"
                        />
                      ) : (
                        <span>{occasionData.imageUrl || 'None'}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Settings Section */}
                <FlyoutGridSection title="SETTINGS">
                  <FlyoutGridRow
                    label="Active Status"
                    value={
                      flyoutMode === 'edit' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isActive}
                            onCheckedChange={(checked) => setEditFormData({ ...editFormData, isActive: checked })}
                          />
                          <span className="text-sm">{editFormData.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      ) : (
                        <span className={occasionData.isActive ? 'text-green-600 font-medium' : 'text-gray-600'}>
                          {occasionData.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === 'view' && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Events"
                      value={<span>{occasionData._count?.events || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Locations"
                      value={<span>{occasionData._count?.locationOccasions || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Topics"
                      value={<span>{occasionData._count?.topicOccasions || 0}</span>}
                    />
                    <FlyoutGridRow
                      label="Services"
                      value={<span>{occasionData._count?.occasionServices || 0}</span>}
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            </>
          ) : null}
        </AdminFlyoutPane>
      </div>
    </DefaultLayout>
  );
}



